'use client';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import axios from 'axios';
import Link from 'next/link';
import Image from 'next/image';
import { toast } from 'react-toastify';

const ForgotPasswordPage = () => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [password1, setPassword1] = useState('');
    const [password2, setPassword2] = useState('');
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [timer, setTimer] = useState(600);
    const [otpSent, setOtpSent] = useState(false);
    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
    const router = useRouter();

    useEffect(() => {
        let interval: number | undefined;

        if (step === 2 && otpSent && timer > 0) {
            interval = window.setInterval(() => {
                setTimer(prev => prev <= 1 ? 0 : prev - 1);
            }, 1000);
        }

        return () => {
            if (interval) window.clearInterval(interval);
        };
    }, [step, timer, otpSent]);

    const clearOtpInput = () => setOtp(['', '', '', '', '', '']);

    const handleOtpChange = (value: string, index: number) => {
        if (value.length > 1) return;
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) otpRefs.current[index + 1]?.focus();
    };

    const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('Text').replace(/\D/g, '').slice(0, 6).split('');
        const newOtp = [...otp];
        pastedData.forEach((char, i) => (newOtp[i] = char));
        setOtp(newOtp);
        otpRefs.current[pastedData.length < 6 ? pastedData.length : 5]?.focus();
    };

    const setRef = useCallback((index: number) => (el: HTMLInputElement | null) => {
        otpRefs.current[index] = el;
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
        if (e.key === 'Enter' && step === 2) handleVerifyOtpAndReset();
    };

    const handleSendOtp = async () => {
        if (!phoneNumber) return toast.error('Утасны дугаараа оруулна уу.');
        setLoading(true);
        try {
            await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/request-password-reset/`, { phone_number: phoneNumber });
            setOtpSent(true);
            setStep(2);
            setTimer(600);
            toast.success('Код илгээлээ');
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.data?.error) {
                toast.error(error.response.data.error); // Display server-side error if available
            } else {
                toast.error('Сүлжээнд холбогдоход алдаа гарлаа. Та дараа дахин оролдоно уу.'); // Default error message
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtpAndReset = async () => {
        const fullOtp = otp.join('');
        if (fullOtp.length !== 6 || !password1 || !password2) {
            toast.error('Мэдээллээ бүрэн оруулна уу.');
            clearOtpInput();
            otpRefs.current[0]?.focus();
            return;
        }
        if (password1 !== password2) {
            toast.error('Нууц үг зөрж байна.');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/verify-password-reset/`, {
                phone_number: phoneNumber,
                otp: fullOtp,
                new_password: password1,
            });
            toast.success('Нууц үг амжилттай солигдлоо');
            router.push('/login');
        } catch (error) {
            toast.error('OTP код буруу байна.');
            clearOtpInput();
            otpRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    };

    return (
        <div className="h-screen w-screen bg-white md:bg-purple-50 dark:md:bg-dark-800 dark:bg-dark-600">
            <div className="flex flex-col justify-center items-center h-screen">
                <div className="p-14">
                    <Link href="/">
                        <div>
                            <Image
                                src="/logo-black-vector.svg"
                                alt="Logo Light Mode"
                                className="dark:hidden"
                                width={260}
                                height={30}
                                priority
                            />
                            <Image
                                src="/logo-white-vector.svg"
                                alt="Logo Dark Mode"
                                className="hidden dark:block"
                                width={260}
                                height={60}
                                priority
                            />
                        </div>
                    </Link>
                </div>

                <div className="w-full max-w-md p-8 bg-white md:shadow-md md:rounded-xl space-y-5 dark:bg-dark-600">
                    {step === 1 && (
                        <>
                            <h1 className="text-2xl font-bold mb-4 text-center">Утасны дугаараа оруулна уу</h1>
                            <input
                                type="number"
                                placeholder="Утасны дугаар"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                className="w-full p-4 border border-gray-300 rounded-lg mb-4 text-dark-700"
                            />
                            <button
                                onClick={handleSendOtp}
                                className="flex items-center justify-center w-full font-bold p-4 text-md bg-purple-500 text-white rounded-lg hover:bg-purple-600"
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="w-6 h-6 border-4 border-t-4 border-white border-solid rounded-full animate-spin"></div>
                                ) : (
                                    'Код илгээх'
                                )}
                            </button>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <h1 className="text-2xl font-bold mb-4 text-center">Кодоо оруулна уу</h1>
                            <div className="flex justify-center space-x-2">
                                {otp.map((value, index) => (
                                    <input
                                        key={index}
                                        type="number"
                                        value={value}
                                        onInput={(e) => handleOtpChange(e.currentTarget.value, index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        onPaste={(e) => handleOtpPaste(e, index)}
                                        ref={setRef(index)}
                                        maxLength={1}
                                        className="w-12 h-12 text-center text-lg font-bold border border-gray-300 rounded-lg text-dark-700"
                                    />
                                ))}
                            </div>
                            <div className="text-center text-lg font-bold mt-2">
                                {timer > 0 ? (
                                    `Үлдсэн хугацаа: ${formatTime(timer)}`
                                ) : (
                                    'Хугацаа дууссан. Дахин код илгээнэ үү.'
                                )}
                            </div>

                            {timer > 0 && (
                                <>
                                    <input
                                        type="password"
                                        placeholder="Шинэ нууц үг"
                                        value={password1}
                                        onChange={(e) => setPassword1(e.target.value)}
                                        className="w-full p-4 border border-gray-300 rounded-lg mt-4  text-dark-700"
                                    />
                                    <input
                                        type="password"
                                        placeholder="Нууц үг баталгаажуулах"
                                        value={password2}
                                        onChange={(e) => setPassword2(e.target.value)}
                                        className="w-full p-4 border border-gray-300 rounded-lg mt-4  text-dark-700"
                                    />
                                    <button
                                        onClick={handleVerifyOtpAndReset}
                                        className="flex items-center justify-center w-full font-bold p-4 text-md bg-purple-500 text-white rounded-lg hover:bg-purple-600 mt-4"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <div className="w-6 h-6 border-4 border-t-4 border-white border-solid rounded-full animate-spin"></div>
                                        ) : (
                                            'Нууц үг сэргээх'
                                        )}
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
